import { html } from 'lit';
import { actionLink } from '../components/actionLink.js';
import { sectionHeading } from '../components/sectionHeading.js';
import type { LandingContent } from '../../shared/content/landingContent.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';

type ConsultationSectionKey = 'consultationApplicant' | 'consultationCompany';

export const consultationSection = (
  section: ConsultationSectionKey,
  content: LandingContent,
  editor?: LandingEditorRenderOptions,
) => {
  const data = content[section];

  return html`
    <section
      class=${editorNodeClass(editor, `section.${section}`, 'vp-section vp-consultation-section')}
      style=${editorNodeStyle(editor, `section.${section}`)}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `section.${section}`, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, `section.${section}`, event)}
    >
      <div class="vp-shell">
        <div
          class=${editorNodeClass(editor, `${section}.panel`, 'vp-consultation-panel reveal-card')}
          style=${editorNodeStyle(editor, `${section}.panel`)}
          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${section}.panel`, event)}
          @click=${(event: MouseEvent) => editorNodeClick(editor, `${section}.panel`, event)}
        >
          <span class="vp-consultation-glow" aria-hidden="true"></span>

          <div class="vp-consultation-copy">
            ${sectionHeading({
              eyebrow: data.eyebrow,
              title: data.title,
              highlight: data.titleHighlight,
              text: data.text,
              editor,
              elementPrefix: section,
            })}

            <div
              class=${editorNodeClass(editor, `${section}.actions`, 'vp-section-actions vp-consultation-actions')}
              style=${editorNodeStyle(editor, `${section}.actions`)}
              @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${section}.actions`, event)}
              @click=${(event: MouseEvent) => editorNodeClick(editor, `${section}.actions`, event)}
            >
              ${actionLink({ action: data.primaryAction, variant: 'primary', editor, elementId: `${section}.primaryAction.label` })}
              ${actionLink({ action: data.secondaryAction, variant: 'light', editor, elementId: `${section}.secondaryAction.label` })}
            </div>

            <p
              class=${editorNodeClass(editor, `${section}.trustLabel`, 'vp-consultation-trust')}
              style=${editorNodeStyle(editor, `${section}.trustLabel`)}
              @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${section}.trustLabel`, event)}
              @click=${(event: MouseEvent) => editorNodeClick(editor, `${section}.trustLabel`, event)}
            >
              <span class="icon-[material-symbols--shield-with-heart]" aria-hidden="true"></span>${data.trustLabel}
            </p>
          </div>

          <a
            class=${editorNodeClass(editor, `${section}.phone`, 'vp-consultation-phone')}
            style=${editorNodeStyle(editor, `${section}.phone`)}
            href=${data.phoneNumber}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${section}.phone`, event)}
            @click=${(event: MouseEvent) => {
              if (editor?.mode === 'editor') event.preventDefault();
              editorNodeClick(editor, `${section}.phone`, event);
            }}
          >
            <span class="icon-[material-symbols--call] vp-consultation-phone-icon" aria-hidden="true"></span>
            <span class="vp-consultation-phone-text">
              <span
                class=${editorNodeClass(editor, `${section}.phoneLabel`, 'vp-consultation-phone-label')}
                style=${editorNodeStyle(editor, `${section}.phoneLabel`)}
                >${data.phoneLabel}</span
              >
              <strong
                class=${editorNodeClass(editor, `${section}.phoneDisplay`, 'vp-consultation-phone-number')}
                style=${editorNodeStyle(editor, `${section}.phoneDisplay`)}
                >${data.phoneDisplay}</strong
              >
            </span>
          </a>
        </div>
      </div>
    </section>
  `;
};
